import React from "react";
import { View, TextInput, StyleSheet } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { setObservationNotes } from "../../store/slices/shiftHandOverSlice";
import LHSectionHeader from "../../components/LHSectionHeader";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../theme";

interface ShiftHandOverNotesProps {
  forceDisabled?: boolean;
}

const ShiftHandOverNotes: React.FC<ShiftHandOverNotesProps> = ({
  forceDisabled = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const dispatch = useDispatch();
  const { handoverState, observationNotes } = useSelector(
    (state: RootState) => state.shiftHandOver
  );

  return (
    <View
      style={[
        styles.sectionContainer,
        isDarkMode && {
          backgroundColor: colors.background.surface,
          borderColor: colors.border.default,
        },
      ]}
    >
      <LHSectionHeader
        title="Observation Notes"
        containerStyle={
          isDarkMode
            ? { ...styles.shiftHeader, backgroundColor: colors.brand.primary }
            : styles.shiftHeader
        }
        textStyle={styles.sectionHeaderText}
        // icon={<MaterialIcons name="note" size={20} color="white" />}
      />
      <View
        style={[
          styles.detailBox,
          isDarkMode && { backgroundColor: colors.background.surface },
        ]}
      >
        <TextInput
          style={[
            styles.notesInput,
            isDarkMode && {
              backgroundColor: colors.background.elevated,
              borderColor: colors.input.border,
              color: colors.text.primary,
            },
            (handoverState === "completed" || forceDisabled) &&
              styles.disabledInput,
            (handoverState === "completed" || forceDisabled) &&
              isDarkMode && {
                backgroundColor: colors.background.elevated,
                borderColor: colors.input.border,
                color: colors.text.disabled,
              },
          ]}
          placeholder="Please record any notes from any selected boxes."
          placeholderTextColor={isDarkMode ? colors.text.disabled : "#868e96"}
          multiline
          numberOfLines={4}
          value={observationNotes}
          onChangeText={(text) => dispatch(setObservationNotes(text))}
          editable={handoverState !== "completed" && !forceDisabled}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  sectionContainer: {
    marginBottom: 12,
    borderRadius: 8,
    backgroundColor: "#fff", // Card background
    borderWidth: 1,
    borderColor: "#e3e6ea", // Subtle border
    shadowColor: 'rgba(0, 94, 192, 0.08)',
    shadowOffset: { width: 4, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 4,
    overflow: "hidden",
    paddingTop: 0, // Remove extra top white space
  },
  shiftHeader: {
    backgroundColor: '#3582E6', // Match Miscellaneous Observations header color
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    paddingVertical: 8, // Match Miscellaneous Observations header height
    paddingHorizontal: 8,
  },
  sectionHeaderText: {
    fontSize: 16,
    color: "white",
    fontWeight: "bold",
  },
  detailBox: {
    padding: 16,
    backgroundColor: "#fff", // White, not gray
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  notesInput: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 6,
    padding: 12,
    minHeight: 100,
    fontSize: 15,
    color: "#495057",
    textAlignVertical: "top",
    backgroundColor: "#fff",
  },
  disabledInput: {
    backgroundColor: "#DADADA",
    borderColor: "#B1B1B1",
    color: "#3A3A3A",
  },
});

export default ShiftHandOverNotes;
